import type { LoadingState } from '@ipfabric/ui-components/utils/loading/withLoadingIndicator';
import { useLocale } from 'containers/App/LocaleContext';
import { useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { useNotifications } from 'utils/hooks/useNotifications';
import { useStateProxy } from 'utils/react/StateProxy';
import { useApi } from 'utils/request/useApi';
import {
  type Delivery,
  type EventType,
  type WebHookRowData,
  type WebHooksState,
  initialWebHooksState,
} from './WebHooksState';
import { makeClearRecentDeliveries } from './commands/clearRecentDeliveries';
import {
  type CreateOrUpdateDelivery,
  makeCreateOrUpdateDelivery,
} from './commands/createOrUpdateDelivery';
import {
  type DeleteDeliveriesCommand,
  makeDeleteDeliveries,
} from './commands/deleteDeliveries';
import {
  type DeleteDeliveriesHistoryCommand,
  makeDeleteDeliveriesHistory,
} from './commands/deleteDeliveriesHistory';
import { makeLoadCACerts } from './commands/loadCACerts';
import {
  type LoadRecentDeliveriesCommand,
  makeLoadRecentDeliveries,
} from './commands/loadRecentDeliveries';
import { makeLoadWebHooks } from './commands/loadWebHooks';
import { makeCloseSavedSecretDialog } from './commands/makeCloseSavedSecretDialog';
import {
  type SaveWebHookCommand,
  makeSaveWebHook,
} from './commands/saveWebHook';
import {
  type TestWebHookCommand,
  makeTestWebHook,
} from './commands/testWebHook';

interface WebHooksCommands {
  clearRecentDeliveries: () => void;
  createOrUpdateDelivery: (command: CreateOrUpdateDelivery) => void;
  deleteDeliveries: (command: DeleteDeliveriesCommand) => void;
  deleteDeliveriesHistory: (command: DeleteDeliveriesHistoryCommand) => void;
  loadCACerts: () => void;
  loadRecentDeliveries: (command: LoadRecentDeliveriesCommand) => void;
  saveWebHook: (command: SaveWebHookCommand) => void;
  testWebHook: (command: TestWebHookCommand) => void;
  closeSavedSecretDialog: () => void;
  loadWebHooks: (options?: { background?: boolean }) => void;
}

export type PrivateWebHooksCommands = WebHooksCommands;

interface WebHooksHook {
  commands: WebHooksCommands;
  dataFetching: boolean;
  webhooks: WebHookRowData[];
  eventTypes: EventType[];
  caCerts: { id: string; name: string }[];
  recentDeliveries: Delivery[] | null;
  deliveriesFetching: boolean;
  savedSecret: string | null;
  detailLoadingState: LoadingState;
}

export function useWebHooks(): WebHooksHook {
  const [getState, updateState] =
    useStateProxy<WebHooksState>(initialWebHooksState);
  const apiRequest = useApi().request;
  const msg = useLocale();
  const { showNotification } = useNotifications();
  const navigate = useNavigate();

  const commands: PrivateWebHooksCommands = useMemo(() => {
    const getCommands = (): PrivateWebHooksCommands => commands;
    return {
      clearRecentDeliveries: makeClearRecentDeliveries(updateState),
      createOrUpdateDelivery: makeCreateOrUpdateDelivery(getState, updateState),
      deleteDeliveries: makeDeleteDeliveries(getState, updateState),
      deleteDeliveriesHistory: makeDeleteDeliveriesHistory(
        apiRequest,
        msg,
        showNotification,
        getCommands,
      ),
      loadCACerts: makeLoadCACerts(updateState, apiRequest),
      loadRecentDeliveries: makeLoadRecentDeliveries(updateState, apiRequest),
      saveWebHook: makeSaveWebHook(
        apiRequest,
        msg,
        showNotification,
        navigate,
        getCommands,
        updateState,
      ),
      testWebHook: makeTestWebHook(apiRequest, msg, showNotification, navigate),
      closeSavedSecretDialog: makeCloseSavedSecretDialog(updateState),
      loadWebHooks: makeLoadWebHooks(updateState, apiRequest),
    };
  }, [apiRequest, msg]);

  // biome-ignore lint/correctness/useExhaustiveDependencies: do only on mount
  useEffect(() => {
    commands.loadWebHooks();
    commands.loadCACerts();
  }, []);

  const {
    dataFetching,
    deliveriesFetching,
    eventTypes,
    caCerts,
    recentDeliveries,
    webhooks,
    savedSecret,
    detailLoadingState,
  } = getState();

  return {
    commands,
    dataFetching,
    deliveriesFetching,
    eventTypes,
    caCerts,
    recentDeliveries,
    webhooks,
    savedSecret,
    detailLoadingState,
  };
}
